import React, { useState, useEffect, createContext } from "react"
import { useShallow } from "zustand/react/shallow"

// components
import Header from "../components/Header/Header"
import BaselineMeasure from "./Measure"

// context
import useAuthoringDataStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringDataStore"
import useResultsTableViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useResultsTableViewStore"
import useAuthoringViewStore from "containers/studies/Study/subcomponents/Authoring/hooks/stores/useAuthoringViewStore"

// utils
import { checkValidAuthoringValue } from "../../../../../utils"

import "./BaselinePreview.scss"

export const BCContext = createContext(null)

const BaselinePreview = () => {
  const { formData } = useAuthoringDataStore(
    useShallow((state) => ({
      formData: state.formData,
    }))
  )

  const { formView } = useAuthoringViewStore(
    useShallow((state) => ({
      formView: state.formView,
    }))
  )

  const { tableView } = useResultsTableViewStore()

  const getDisplayValue = (value) => {
    if (value === undefined || value === null || value === "") return "-"
    return value
  }

  let bgColor = "rgb(217, 228, 253)"
  let arms = []
  let sas = []
  let colSpan = 2

  const [tables, setTables] = useState([])

  if (formData?.results?.baseline_characteristics?.baseline_characteristics_table?.[tableView]?.arms) {
    arms = formData.results.baseline_characteristics.baseline_characteristics_table[tableView].arms
    colSpan += arms.length
  }

  if (formData?.results?.baseline_characteristics?.baseline_characteristics_table?.[tableView]?.subject_analysis_sets) {
    sas = formData.results.baseline_characteristics.baseline_characteristics_table[tableView].subject_analysis_sets
    colSpan += sas.length
  }

  useEffect(() => {
    const canShowSasColumns = (sasIdSet, measures) => {
      let hasSasData = false

      if (measures) {
        measures.forEach((measure) =>
          measure.rows.forEach((row) => {
            row.categories.forEach((category) => {
              category.values.forEach((value) => {
                if (
                  sasIdSet.has(value.arm_id) &&
                  (checkValidAuthoringValue(value.value) ||
                    checkValidAuthoringValue(value.dispersion_value) ||
                    checkValidAuthoringValue(value.dispersion_value_upper_bound))
                ) {
                  hasSasData = true
                }
              })
            })
          })
        )
      }

      return hasSasData
    }

    const baselineCharacteristics =
      formData?.results?.baseline_characteristics?.baseline_characteristics_table?.[tableView]

    if (baselineCharacteristics) {
      let measures = baselineCharacteristics?.baseline_measures
      let armsToDisplay = [...arms]
      const tables = []

      let startIndex = 0
      let endIndex = 5
      let hasSasData = false
      let sasIdSet = new Set()

      if (sas) {
        sasIdSet = new Set(sas?.map((group) => group.id) || [])
      }

      if (sas?.length > 0 && tableView === "eudract") {
        hasSasData = canShowSasColumns(sasIdSet, measures)
      }

      if (hasSasData) {
        armsToDisplay = [...arms, ...sas]
      } else {
        measures = measures.map((measure) => {
          return {
            ...measure,
            rows: measure.rows.map((row) => {
              return {
                ...row,
                categories: row.categories.map((category) => {
                  return {
                    ...category,
                    values: category.values.filter((value) => {
                      return !sasIdSet.has(value.arm_id)
                    }),
                  }
                }),
              }
            }),
          }
        })
      }

      if (armsToDisplay.length > endIndex) {
        while (armsToDisplay.length > startIndex) {
          if (endIndex >= armsToDisplay.length) endIndex = armsToDisplay.length
          let table = {
            ...baselineCharacteristics,
            armsToDisplay: armsToDisplay.slice(startIndex, endIndex),
            baseline_measures: measures?.map((measure) => {
              return {
                ...measure,
                rows: measure.rows.map((row) => {
                  return {
                    ...row,
                    categories: row.categories.map((category) => {
                      return {
                        ...category,
                        values: category.values.slice(startIndex, endIndex),
                      }
                    }),
                  }
                }),
              }
            }),
          }

          tables.push(table)
          startIndex = endIndex
          endIndex += 5
        }
      } else {
        tables.push({
          arms: armsToDisplay,
          armsToDisplay: armsToDisplay,
          baseline_measures: measures,
        })
      }

      setTables(tables)
    }
  }, [arms.length, formData?.results?.baseline_characteristics?.baseline_characteristics_table])

  let firstColumnWidth = 250

  return (
    <BCContext.Provider
      value={{
        view: tableView,
        data: formData?.results?.baseline_characteristics?.baseline_characteristics_table,
      }}
    >
      <Header
        title="Reporting Groups"
        bgColor={bgColor}
        arms={arms}
        view={tableView}
      />

      {tableView === "eudract" && (
        <Header
          title={"Subject Analysis Sets"}
          bgColor={bgColor}
          subjectAnalysisSets={sas || null}
          view={tableView}
        />
      )}

      {tables.map((table, i) => {
        let totalArmsParticipants = arms?.reduce((acc, arm) => {
          if (arm.baseline_analysis_population) {
            return acc + Number(arm.baseline_analysis_population)
          }
          return acc
        }, 0)

        let totalSasParticipants = sas?.reduce((acc, sas) => {
          if (sas.baseline_analysis_population) {
            return acc + Number(sas.baseline_analysis_population)
          }
          return acc
        }, 0)

        return (
          <table
            key={`baseline-table-${i} results-table`}
            className="baseline-table"
          >
            <tbody>
              <tr style={{ backgroundColor: bgColor, fontWeight: "bold" }}>
                <td
                  colSpan={2}
                  style={{ width: firstColumnWidth * 2 }}
                />
                {table?.armsToDisplay?.map((arm) => (
                  <td key={arm.id}>{arm.title || "(Title)"}</td>
                ))}
                {tables.length - 1 === i && <td>TOTAL</td>}
              </tr>
              <tr>
                <td
                  colSpan={2}
                  style={{ backgroundColor: bgColor, fontWeight: "bold", width: firstColumnWidth * 2 }}
                >
                  Overall Number of Baseline Participants
                </td>
                {table?.armsToDisplay?.map((arm) => (
                  <td key={arm.id}>{getDisplayValue(arm.baseline_analysis_population)}</td>
                ))}
                {tables.length - 1 === i && <td>{totalArmsParticipants + totalSasParticipants}</td>}
              </tr>

              {tableView === "ctg" &&
                formData?.results?.baseline_characteristics?.baseline_characteristics_table?.ctg
                  ?.type_units_analyzed && (
                  <tr>
                    <td
                      colSpan={2}
                      style={{ backgroundColor: bgColor, fontWeight: "bold", width: firstColumnWidth * 2 }}
                    >
                      Overall Number of Units Analyzed
                    </td>
                    {table?.armsToDisplay?.map((arm) => (
                      <td key={arm.id}>{getDisplayValue(arm.num_units_analyzed)}</td>
                    ))}
                    {tables.length - 1 === i && (
                      <td>
                        {arms?.reduce((acc, arm) => {
                          if (arm.num_units_analyzed) {
                            return acc + Number(arm.num_units_analyzed)
                          }
                          return acc
                        }, 0)}
                      </td>
                    )}
                  </tr>
                )}
              {tableView === "ctg" && (
                <tr>
                  <td
                    colSpan={2}
                    style={{ backgroundColor: bgColor, fontWeight: "bold", width: firstColumnWidth * 2 }}
                  >
                    Type of Units Analyzed
                  </td>
                  <td colSpan={arms.length + 1}>
                    {
                      formData?.results?.baseline_characteristics?.baseline_characteristics_table?.ctg
                        ?.type_units_analyzed
                    }
                  </td>
                </tr>
              )}
              {tableView === "ctg" && (
                <tr>
                  <td
                    colSpan={2}
                    style={{ backgroundColor: bgColor, fontWeight: "bold", width: firstColumnWidth * 2 }}
                  >
                    Baseline Analysis Population Description
                  </td>
                  <td colSpan={arms.length + 1}>
                    {
                      formData?.results?.baseline_characteristics?.baseline_characteristics_table?.ctg
                        ?.population_analysis_description
                    }
                  </td>
                </tr>
              )}

              {table?.baseline_measures?.map((bm) => (
                <BaselineMeasure
                  key={bm.id}
                  bmId={bm.id}
                  showTotal={tables.length - 1 === i}
                  arms={table.armsToDisplay}
                  firstColumnWidth={firstColumnWidth}
                  bgColor={bgColor}
                  data={bm}
                  baselineData={formData?.results?.baseline_characteristics?.baseline_characteristics_table}
                  formData={formData?.results?.baseline_characteristics?.baseline_characteristics_table?.[tableView]}
                  view={tableView}
                  typeOfUnits={
                    formData?.results?.baseline_characteristics?.baseline_characteristics_table?.ctg
                      ?.type_units_analyzed
                  }
                />
              ))}
            </tbody>
          </table>
        )
      })}
    </BCContext.Provider>
  )
}

export default BaselinePreview
